import Page from '../Page';
import ProductPageSkeleton from '../components/ProductPageSkeleton';
import ConfigurationHealth from '../components/ConfigurationHealth';
import ComponentGroupField from '../components/ComponentGroupField';
import {newCustomOption, newIncludedItem, hideModal} from '../../../../shared/editor-defaults.js';
import {useEffect, useRef, useState} from 'preact/hooks';
import {pageSettings} from '../../../../shared/page-settings.js';
import {createId, prepareGroupConfigurationForSave, pruneComponentOrder} from '../../../../shared/configurator';
import {ensureConfiguratorDefinitions, fetchProductConfiguration, saveProductConfiguration, resetProductDraft} from '../../../../shared/models/product-configurator';
import {fetchSwitcherSession, prepareSwitcherSave, switcherDirty, switcherUnpublished, refreshAfterGroupSave, publishSwitcherToGroup} from '../../../../shared/models/product-switcher';
import {publishProductPage} from '../../../../shared/models/product-page-publication';
import CustomOptionEditor from '../components/CustomOptionEditor.jsx';
import ProductSwitcherEditor, {ProductSwitcherRow} from '../components/ProductSwitcherEditor.jsx';
import ProductGroupCard from '../components/ProductGroupCard.jsx';
import DisclaimersCard from '../components/DisclaimersCard.jsx';
import {SortControls, SortModeButton} from '../components/SortableTable.jsx';

export default function ProductPage({id}) {
  const productId = decodeURIComponent(id);
  const [product, setProduct] = useState(null);
  const [switcher, setSwitcher] = useState(null);
  const [switcherBusy, setSwitcherBusy] = useState(false);
  const [disclaimerBusy, setDisclaimerBusy] = useState(false);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const resetModal = useRef(null);
  const [resetError, setResetError] = useState('');
  const [editingItemKey, setEditingItemKey] = useState('');
  const [editingIncludeItemId, setEditingIncludeItemId] = useState('');
  const [isReordering, setIsReordering] = useState(false);
  const [isPackingReordering, setIsPackingReordering] = useState(false);
  const [variantDetails, setVariantDetails] = useState({});
  const [dirtyGroupIds, setDirtyGroupIds] = useState(new Set());
  const [deletingGroupId, setDeletingGroupId] = useState('');

  useEffect(() => {
    setProduct(null); setSwitcher(null); setStatus('loading');
    ensureConfiguratorDefinitions().then(async () => {
      const nextProduct = await fetchProductConfiguration(productId);
      return [nextProduct, await fetchSwitcherSession(productId, false, nextProduct)];
    }).then(([nextProduct, nextSwitcher]) => {
      setProduct(nextProduct);
      setSwitcher(nextSwitcher);
      setVariantDetails(Object.fromEntries(nextProduct.componentVariants.map((variant) => [variant.id, variant])));
    })
      .catch((e) => setError(e instanceof Error ? e.message : 'Unable to load the product.'))
      .finally(() => setStatus('idle'));
  }, [productId]);

  const updateConfiguration = (mutator) => {
    setProduct((currentProduct) => {
      const configuration = structuredClone(currentProduct.configuration);
      mutator(configuration);
      pruneComponentOrder(configuration);
      return {...currentProduct, configuration};
    });
  };
  const markGroupDirty = (groupId) => setDirtyGroupIds((current) => new Set(current).add(groupId));
  const save = async (event) => {
    const promise = (async () => {
      setStatus('saving'); setError('');
      try {
        let prepared = product.configuration;
        for (const groupId of dirtyGroupIds) {
          if (prepared.customOptions.some(({id}) => id === groupId)) prepared = prepareGroupConfigurationForSave(prepared, groupId);
        }
        const saved = await saveProductConfiguration(product.id, prepareSwitcherSave(switcher, prepared), product.digests);
        setProduct((current) => ({...current, ...saved, configuration: current.configuration === product.configuration ? saved.configuration : current.configuration}));
        setSwitcher(await fetchSwitcherSession(product.id));
        shopify.toast.show('Draft saved. Published configuration unchanged.');
      } catch (e) { setError(e instanceof Error ? e.message : 'Unable to save the configuration.'); throw e; }
      finally { setStatus('idle'); }
    })();
    event?.waitUntil?.(promise); await promise;
  };
  const publish = async () => {
    if (status !== 'idle') return;
    setStatus('publishing'); setError('');
    try {
      let prepared = product.configuration;
      for (const groupId of dirtyGroupIds) {
        if (prepared.customOptions.some(({id}) => id === groupId)) prepared = prepareGroupConfigurationForSave(prepared, groupId);
      }
      const saved = await saveProductConfiguration(product.id, prepareSwitcherSave(switcher, prepared), product.digests);
      setProduct((current) => ({...current, ...saved, configuration: current.configuration === product.configuration ? saved.configuration : current.configuration}));
      const savedSwitcher = await fetchSwitcherSession(product.id);
      setSwitcher(savedSwitcher);
      const published = await publishProductPage({...product, ...saved}, savedSwitcher);
      setProduct((current) => ({...current, ...published, configuration: current.configuration === saved.configuration ? published.configuration : current.configuration}));
      setSwitcher(await fetchSwitcherSession(product.id));
      shopify.toast.show('Configuration published');
    } catch (e) { setError(e instanceof Error ? e.message : 'Unable to publish.'); }
    finally { setStatus('idle'); }
  };
  const resetDraft = async () => {
    if (status !== 'idle' || !product.publishedConfiguration) return;
    setResetError('');
    setStatus('resetting'); setError('');
    try {
      const saved = await resetProductDraft(product);
      setProduct((current) => ({...current, ...saved, configuration: current.configuration === product.configuration ? saved.configuration : current.configuration}));
      setSwitcher(await fetchSwitcherSession(product.id));
      resetModal.current?.hideOverlay(); setEditingItemKey(''); setEditingIncludeItemId('');
      shopify.toast.show('Draft reset to published configuration');
    } catch (e) { setResetError(e instanceof Error ? e.message : 'Unable to reset draft.'); }
    finally { setStatus('idle'); }
  };
  const publishSwitcher = async () => {
    if (status !== 'idle' || switcherBusy || disclaimerBusy) return;
    setStatus('publishing-switcher'); setError('');
    try {
      setSwitcher(await publishSwitcherToGroup(switcher));
      hideModal('publish-switcher-modal');
      shopify.toast.show('Switcher published to group');
    } catch (e) {
      hideModal('publish-switcher-modal');
      setError(e instanceof Error ? e.message : 'Unable to publish the switcher.');
    } finally { setStatus('idle'); }
  };
  const addGroup = () => {
    const group = newAddonGroup(product.configuration.customOptions);
    updateConfiguration((configuration) => {
      configuration.customOptions.push(group);
      configuration.formOrder.push({type: 'customOption', id: group.id});
    });
    markGroupDirty(group.id);
    setEditingItemKey(`customOption:${group.id}`);
  };
  if (!product) return <Page>{error ? <s-banner tone="critical">{error}</s-banner> : <ProductPageSkeleton />}</Page>;
  const includedItems = product.configuration.includedItems;
  const settings = pageSettings(product.configuration);
  const setPackingListDisplay = (display) => updateConfiguration((configuration) => {
    configuration.settings = pageSettings(configuration);
    configuration.settings.packingListDisplay = display;
    configuration.settings.pageFeatures.packingList = display !== 'hidden';
  });
  const unsaved = JSON.stringify(product.configuration) !== product.savedConfiguration || switcherDirty(switcher);
  const unpublished = Boolean(product.savedDraft) && JSON.stringify(product.savedDraft) !== JSON.stringify(product.publishedConfiguration) || switcherUnpublished(switcher);
  const busy = status !== 'idle' || switcherBusy || disclaimerBusy;
  const publicationStatus = unsaved
    ? {label: 'Unsaved changes', tone: 'warning'}
    : unpublished || !product.publishedConfiguration
      ? {label: 'Unpublished changes', tone: 'info'}
      : {label: 'Published', tone: 'success'};
  const canReset = !busy && Boolean(product.publishedConfiguration) && (unsaved || unpublished || switcher?.stale);
  return (
    <Page heading={product.title} inlineSize="base">
      <s-link slot="breadcrumb-actions" href="/products">Products</s-link>
      <s-button slot="primary-action" variant="primary" onClick={save} loading={status === 'saving'} disabled={busy}>Save draft</s-button>
      <s-button slot="secondary-actions" commandFor="product-more-actions">More actions</s-button>
      <s-menu id="product-more-actions" accessibilityLabel="Product actions">
        <s-button href={product.previewUrl} target="_blank" disabled={busy || unsaved || !product.savedDraft}>Preview draft</s-button>
        <s-button href={product.publishedUrl} target="_blank">View published</s-button>
        <s-button href={product.adminUrl} target="_blank">Edit in admin</s-button>
      </s-menu>
      {error && <s-banner tone="critical">{error}</s-banner>}
      <ConfigurationHealth product={product} />
      <s-query-container>
      <s-stack direction="block" gap="base">
        <s-grid gridTemplateColumns="@container (inline-size > 800px) 2fr 1fr, 1fr" gap="base" alignItems="start">
        <s-stack direction="block" gap="base">
      <s-section>
        <s-stack direction="block" gap="small">
          <s-grid gridTemplateColumns="minmax(0, 1fr) auto" gap="small" alignItems="center">
            <s-heading>Form options</s-heading>
            <s-stack direction="inline" gap="small-100">
            <s-button variant="tertiary" icon="settings" accessibilityLabel="Form options settings" commandFor="form-settings-modal" command="--show" disabled={busy} />
            </s-stack>
          </s-grid>
          <s-table>
            <s-table-header-row>
              <s-table-header listSlot="primary">Option</s-table-header>
              <s-table-header listSlot="secondary">Type</s-table-header>
              <s-table-header listSlot="labeled">Details</s-table-header>
              <s-table-header listSlot="inline" format="numeric"><s-stack direction="inline" justifyContent="end"><s-text accessibilityVisibility="exclusive">Actions</s-text><SortModeButton sorting={isReordering} label="options" onChange={(sorting) => { setEditingItemKey(''); setIsReordering(sorting); }} /></s-stack></s-table-header>
            </s-table-header-row>
            <s-table-body>
            {switcher?.published && <ProductSwitcherRow session={switcher} editing={editingItemKey === 'switcher'} sorting={isReordering} disabled={busy} onEdit={() => setEditingItemKey(editingItemKey === 'switcher' ? '' : 'switcher')} />}
            {product.configuration.formOrder.map((item, index) => {
              const itemKey = orderKey(item);
              const isEditing = editingItemKey === itemKey;
              return (
                <s-table-row key={`row-${index}`}>
                  <s-table-cell><div style={{display:'flex', alignItems:'center', gap:'8px', minWidth:0}}>{isReordering && <SortControls index={index} count={product.configuration.formOrder.length} label={orderLabel(item, product)} onMove={(from, to) => updateConfiguration((configuration) => moveTo(configuration.formOrder, from, to))} />}<div style={{minWidth:0, overflowWrap:'anywhere'}}><s-text type="strong">{orderLabel(item, product)}</s-text></div></div></s-table-cell>
                  <s-table-cell><span style={{whiteSpace: 'nowrap'}}><s-text color="subdued">{orderType(item)}</s-text></span></s-table-cell>
                  <s-table-cell><s-text color="subdued">{orderSummary(item, product)}</s-text></s-table-cell>
                  <s-table-cell>
                    <s-stack direction="inline" gap="small-100" justifyContent="end">
                      {!isReordering && (isEditing
                        ? <s-button variant="tertiary" icon="check" accessibilityLabel={`Done editing ${orderLabel(item, product)}`} onClick={() => setEditingItemKey('')} />
                        : <s-button variant="tertiary" icon="edit" accessibilityLabel={`Edit ${orderLabel(item, product)}`} onClick={() => setEditingItemKey(itemKey)} />)}
                    </s-stack>
                  </s-table-cell>
                </s-table-row>
              );
            })}
            </s-table-body>
          </s-table>
          <ProductSwitcherEditor key={`${product.id}:${switcher?.published?.id || ''}`} session={switcher} enabled={Boolean(switcher?.published) && editingItemKey === 'switcher'} disabled={busy} publishing={status === 'publishing-switcher'} onPublish={publishSwitcher} onChange={setSwitcher} />
          {product.configuration.formOrder.filter((item) => orderKey(item) === editingItemKey).map((item) => {
            const groupIndex = item.type === 'customOption' ? product.configuration.customOptions.findIndex(({id}) => id === item.id) : -1;
            return <s-stack key={editingItemKey} direction="block" gap="base">
              <s-divider />
              <s-grid gridTemplateColumns="1fr auto" gap="small" alignItems="center">
                <s-heading>{orderLabel(item, product)}</s-heading>
                <s-stack direction="inline" gap="small-100">
                  {item.type === 'customOption' && <s-button variant="tertiary" icon="delete" accessibilityLabel={`Remove ${orderLabel(item, product)}`} commandFor="remove-custom-option-modal" command="--show" onClick={() => setDeletingGroupId(item.id)} />}
                  <s-button variant="tertiary" onClick={() => setEditingItemKey('')}>Done</s-button>
                </s-stack>
              </s-grid>
                    {item.type === 'shopifyOption' && <NativeOptionEditor product={product} optionId={item.id} onUpdate={updateConfiguration} />}
                    {item.type === 'customOption' && groupIndex >= 0 && <CustomOptionEditor
                      product={product}
                      groupIndex={groupIndex}
                      variantDetails={variantDetails}
                      onVariantsFound={(variants) => setVariantDetails((current) => ({...current, ...Object.fromEntries(variants.map((variant) => [variant.id, variant]))}))}
                      onChange={(nextGroup) => {
                        updateConfiguration((configuration) => { configuration.customOptions[groupIndex] = nextGroup; });
                        markGroupDirty(item.id);
                      }}
                    />}
            </s-stack>;
          })}
          {!editingItemKey && <s-button onClick={addGroup}>Add custom option</s-button>}
        </s-stack>
      </s-section>

      <DisclaimersCard key={product.id} ids={product.configuration.disclaimerHandles || []} required={settings.requireTermsAcceptance} disabled={busy}
        onBusyChange={setDisclaimerBusy}
        onRequiredChange={(required) => updateConfiguration((configuration) => {
          configuration.settings = pageSettings(configuration);
          configuration.settings.requireTermsAcceptance = required;
        })}
        onChange={(handles) => updateConfiguration((configuration) => { configuration.disclaimerHandles = handles; })} />

      <s-section>
        <ItemList
          items={includedItems}
          product={product}
          editingItemId={editingIncludeItemId}
          sorting={isPackingReordering}
          onSortingChange={(sorting) => { setEditingIncludeItemId(''); setIsPackingReordering(sorting); }}
          onEdit={setEditingIncludeItemId}
          onAdd={(item) => {
            updateConfiguration((configuration) => { configuration.includedItems.push(item); configuration.includedItemOrder.push(`include:${item.id}`); });
          }}
          onUpdate={(itemId, mutator) => updateConfiguration((configuration) => {
            const item = configuration.includedItems.find(({id}) => id === itemId);
            if (item) mutator(item);
          })}
          onUpdateVariant={(variantId, itemLabel, componentGroup) => updateConfiguration((configuration) => {
            for (const group of configuration.customOptions) for (const choice of group.choices || []) for (const component of choice.components || []) {
              if (component.variantId === variantId) { component.itemLabel = itemLabel; component.componentGroup = componentGroup; }
            }
          })}
          onRemove={(itemId) => {
            updateConfiguration((configuration) => removeIncludeItem(configuration, itemId));
            setEditingIncludeItemId('');
          }}
          onReorder={(includedItemOrder) => updateConfiguration((configuration) => { configuration.includedItemOrder = includedItemOrder; })}
          display={settings.packingListDisplay}
          title={settings.packingListTitle}
          onTitleChange={(title) => updateConfiguration((configuration) => { configuration.settings = {...pageSettings(configuration), packingListTitle: title}; })}
          onDisplayChange={setPackingListDisplay}
        />
      </s-section>

        </s-stack>
        <s-stack direction="block" gap="base">
        <s-section>
          <s-stack gap="small">
            <s-grid gridTemplateColumns="1fr auto" gap="small" alignItems="center">
              <s-heading>Status</s-heading>
              <s-badge tone={unsaved ? 'warning' : unpublished || !product.publishedConfiguration ? 'info' : 'success'}>{publicationStatus.label}</s-badge>
            </s-grid>
            <s-text>Save draft to preview changes, publish to push changes live.</s-text>
            <s-stack direction="inline" gap="small" justifyContent="start">
              <s-button variant="secondary" onClick={save} loading={status === 'saving'} disabled={busy}>Save draft</s-button>
              <s-button variant="secondary" onClick={publish} loading={status === 'publishing'} disabled={busy}>Publish</s-button>
              <s-button variant="tertiary" tone="critical" commandFor="reset-draft-modal" command="--show" onClick={() => setResetError('')} disabled={!canReset}>Reset</s-button>
            </s-stack>
          </s-stack>
        </s-section>
        <ProductGroupCard key={product.id} session={switcher} adminUrl={product.adminUrl} disabled={status !== 'idle'} onBusyChange={setSwitcherBusy} onSaved={(fresh) => setSwitcher((previous) => refreshAfterGroupSave(previous, fresh))} />
        </s-stack>
        </s-grid>
      </s-stack>
      </s-query-container>
      <s-modal id="form-settings-modal" heading="Form options settings">
        <s-stack direction="block" gap="base">
        <s-choice-list label="Form style" name="form-style" values={[settings.formStyle]} disabled={busy} onChange={(event) => {
          const formStyle = event.currentTarget.values[0];
          updateConfiguration((configuration) => { configuration.settings = {...pageSettings(configuration), formStyle}; });
        }}>
          <s-choice value="simple">Simple</s-choice>
          <s-choice value="advanced">Advanced</s-choice>
          <s-choice value="blocks">Blocks</s-choice>
        </s-choice-list>
        </s-stack>
        <s-button slot="primary-action" variant="primary" commandFor="form-settings-modal" command="--hide">Done</s-button>
      </s-modal>
      <s-modal ref={resetModal} id="reset-draft-modal" heading="Reset changes">
        <s-paragraph>This will reset changes to the published version.</s-paragraph>
        {resetError && <s-banner tone="critical">{resetError}</s-banner>}
        <s-button slot="secondary-actions" commandFor="reset-draft-modal" command="--hide" disabled={busy}>Cancel</s-button>
        <s-button slot="primary-action" variant="primary" tone="critical" onClick={resetDraft} loading={status === 'resetting'} disabled={!canReset}>Reset</s-button>
      </s-modal>
      <s-modal id="remove-custom-option-modal" heading="Remove custom option">
        <s-paragraph>Are you sure you want to remove this option from the product form?</s-paragraph>
        <s-button slot="secondary-actions" commandFor="remove-custom-option-modal" command="--hide" onClick={() => setDeletingGroupId('')}>Cancel</s-button>
        <s-button slot="primary-action" variant="primary" tone="critical" commandFor="remove-custom-option-modal" command="--hide" disabled={!deletingGroupId} onClick={() => {
          if (!deletingGroupId) return;
          updateConfiguration((configuration) => removeAddonGroup(configuration, deletingGroupId));
          setDirtyGroupIds((current) => { const next = new Set(current); next.delete(deletingGroupId); return next; });
          if (editingItemKey === `customOption:${deletingGroupId}`) setEditingItemKey('');
          setDeletingGroupId('');
        }}>Remove option</s-button>
      </s-modal>
    </Page>
  );
}

function ItemList({items, product, editingItemId, sorting, onSortingChange, onEdit, onAdd, onUpdate, onUpdateVariant, onRemove, onReorder, display, onDisplayChange, title, onTitleChange}) {
  const editorModal = useRef(null);
  const removeModal = useRef(null);
  const [draft, setDraft] = useState(null);
  const customItemsByVariant = new Map();
  for (const group of product.configuration.customOptions) for (const choice of group.choices || []) for (const component of choice.components || []) {
    const current = customItemsByVariant.get(component.variantId) || {...component, choiceIds: new Set()};
    current.choiceIds.add(choice.id); current.itemLabel ||= component.itemLabel; current.componentGroup ||= component.componentGroup;
    customItemsByVariant.set(component.variantId, current);
  }
  const totalChoices = product.configuration.shopifyOptions.reduce((sum, option) => sum + (option.optionValueIds || []).length, 0) + product.configuration.customOptions.reduce((sum, group) => sum + (group.choices || []).length, 0);
  const itemRows = items.map((item) => ({key: `include:${item.id}`, item, label: item.label, choices: item.type === 'shopifyOption' ? (item.optionValueIds || []).length : totalChoices, custom: false}));
  itemRows.push(...[...customItemsByVariant.values()].map((item) => ({key: `custom:${item.variantId}`, item, label: item.itemLabel || 'Component label required', choices: item.choiceIds.size, custom: true})));
  const rowsByKey = new Map();
  for (const row of itemRows) rowsByKey.set(row.key, row);
  const orderedKeys = [...(product.configuration.includedItemOrder || []).filter((key) => rowsByKey.has(key)), ...[...rowsByKey.keys()].filter((key) => !(product.configuration.includedItemOrder || []).includes(key))];
  const rows = orderedKeys.map((key) => rowsByKey.get(key));
  const componentGroups = [...new Set(rows.map(({item}) => item.componentGroup?.trim()).filter(Boolean))];
  const editingRow = rowsByKey.get(editingItemId);
  const editingItem = draft;
  const addingItem = editingItemId.startsWith('new:');
  const componentSource = editingRow?.custom ? 'customOption' : editingItem?.type || 'base';
  const editingVariant = editingRow?.custom ? product.componentVariants.find(({id}) => id === editingItem?.variantId) : null;
  const updateDraft = (mutator) => setDraft((current) => { const next = structuredClone(current); mutator(next); return next; });
  const openEditor = (row) => { setDraft(structuredClone(row.custom ? {...row.item, type: 'customOption'} : row.item)); onEdit(row.key); };
  const addItem = () => {
    const item = newIncludedItem(items);
    setDraft(item); onEdit(`new:${item.id}`);
  };
  const finishEditing = () => {
    if (!draft) return;
    draft.componentGroup = draft.componentGroup?.trim() || '';
    if (editingRow?.custom) onUpdateVariant(draft.variantId, draft.itemLabel, draft.componentGroup);
    else if (editingItemId.startsWith('new:')) onAdd(draft);
    else onUpdate(draft.id, (item) => Object.assign(item, draft));
    setDraft(null); onEdit('');
  };
  return (
    <s-stack direction="block" gap="small">
      <s-grid gridTemplateColumns="1fr auto" gap="small" alignItems="center"><s-heading>Components</s-heading><s-button variant="tertiary" icon="settings" accessibilityLabel="Component list settings" commandFor="packing-list-settings-modal" command="--show" /></s-grid>
      <s-table><s-table-header-row><s-table-header listSlot="primary">Component</s-table-header><s-table-header listSlot="labeled">Source</s-table-header><s-table-header listSlot="labeled" format="numeric">Choices</s-table-header><s-table-header listSlot="labeled">Group</s-table-header><s-table-header listSlot="inline" format="numeric"><s-stack direction="inline" justifyContent="end"><s-text accessibilityVisibility="exclusive">Actions</s-text><SortModeButton sorting={sorting} disabled={rows.length < 2} label="components" onChange={onSortingChange} /></s-stack></s-table-header></s-table-header-row><s-table-body>
        {rows.map((row, index) => <s-table-row key={index}><s-table-cell><div style={{display:'flex', alignItems:'center', gap:'8px', minWidth:0}}>{sorting && <SortControls index={index} count={rows.length} label={row.label} onMove={(from, to) => { const next = [...orderedKeys]; moveTo(next, from, to); onReorder(next); }} />}<div style={{minWidth:0, overflowWrap:'anywhere'}}><s-text type="strong">{row.label}</s-text></div></div></s-table-cell><s-table-cell><span style={{whiteSpace: 'nowrap'}}>{row.custom ? 'Custom option' : row.item.type === 'shopifyOption' ? 'Shopify option' : 'Base'}</span></s-table-cell><s-table-cell>{row.choices} / {totalChoices}</s-table-cell><s-table-cell>{row.item.componentGroup?.trim() || '—'}</s-table-cell><s-table-cell><s-stack direction="inline" gap="small-100" justifyContent="end">{!sorting && <s-button variant="tertiary" icon="edit" accessibilityLabel={`Edit ${row.label}`} commandFor="packing-item-editor" command="--show" onClick={() => openEditor(row)} />}</s-stack></s-table-cell></s-table-row>)}
      {!rows.length && <s-table-row><s-table-cell><s-text color="subdued">No components yet.</s-text></s-table-cell><s-table-cell /><s-table-cell /><s-table-cell /><s-table-cell /></s-table-row>}</s-table-body></s-table>
      {!sorting && <s-button commandFor="packing-item-editor" command="--show" onClick={addItem}>Add component</s-button>}
      <s-modal id="packing-list-settings-modal" heading="Component list settings">
        <s-stack direction="block" gap="base">
          <s-text-field label="Title" name="packing-list-title" value={title} placeholder="Included" details="Optional override for the title of the component list." onInput={(event) => onTitleChange(event.currentTarget.value)} />
          <s-choice-list label="Display" name="packing-list-display" values={[display]} onChange={(event) => onDisplayChange(event.currentTarget.values[0])}><s-choice value="belowForm">Below form</s-choice><s-choice value="belowImage">Below image</s-choice><s-choice value="hidden">Hidden</s-choice></s-choice-list>
        </s-stack>
        <s-button slot="primary-action" variant="primary" commandFor="packing-list-settings-modal" command="--hide">Done</s-button>
      </s-modal>
      <s-modal ref={editorModal} id="packing-item-editor" heading={editingItemId.startsWith('new:') ? 'Add component' : 'Edit component'}>
        {editingItem && <s-stack direction="block" gap="base">
          <s-stack direction="block" gap="small-100">
            <s-text-field label="Component label" value={editingRow?.custom ? editingItem.itemLabel || '' : editingItem.label} onInput={(event) => updateDraft((item) => { if (editingRow?.custom) item.itemLabel = event.currentTarget.value; else item.label = event.currentTarget.value; })} required />
            {editingRow?.custom && <s-text color="subdued">{editingVariant ? `${editingVariant.product?.title || 'Product'} — ${editingVariant.title || 'Variant'}` : editingItem.variantId}</s-text>}
          </s-stack>
          <s-select label="Component source" name="packing-item-type" disabled={!addingItem} value={componentSource} onChange={(event) => { const source = event.currentTarget.value; if (addingItem && ['base', 'shopifyOption'].includes(source)) updateDraft((item) => { item.type = source; }); }}>
            <s-option value="base">Base</s-option>
            <s-option value="shopifyOption">Shopify option</s-option>
            <s-option value="customOption" disabled>Custom option (automatic)</s-option>
          </s-select>
          <ComponentGroupField value={editingItem.componentGroup || ''} groups={componentGroups} onChange={(value) => updateDraft((item) => { item.componentGroup = value; })} />
          {!editingRow?.custom && editingItem.type === 'shopifyOption' && <s-stack gap="base"><s-text type="strong">Active for option values</s-text>{product.options.map((option) => <s-box key={option.id} padding="base" background="subdued" borderRadius="base"><s-stack gap="small"><s-text type="strong">{option.name}</s-text>{option.optionValues.map((value) => { const valueId = numericId(value.id); const selected = (editingItem.optionValueIds || []).includes(valueId); return <s-checkbox key={value.id} label={value.name} checked={selected} onChange={(event) => updateDraft((item) => { item.optionValueIds ||= []; item.optionValueIds = event.currentTarget.checked ? [...new Set([...item.optionValueIds, valueId])] : item.optionValueIds.filter((id) => id !== valueId); })} />; })}</s-stack></s-box>)}</s-stack>}
          <s-text-field label="Link (optional)" details="Use a /products/... path or an https:// URL." value={editingRow?.custom && editingVariant ? `/products/${editingVariant.product.handle}?variant=${numericId(editingVariant.id)}` : editingItem.url || ''} disabled={editingRow?.custom} onInput={(event) => updateDraft((item) => { item.url = event.currentTarget.value; })} />
        </s-stack>}
        {editingItem && !editingRow?.custom && !editingItemId.startsWith('new:') && <s-button slot="secondary-actions" onClick={() => removeModal.current?.showOverlay()}>Remove</s-button>}
        <s-button slot="primary-action" variant="primary" commandFor="packing-item-editor" command="--hide" onClick={finishEditing}>Done</s-button>
      </s-modal>
      {editingItem && !editingRow?.custom && !editingItemId.startsWith('new:') && <s-modal ref={removeModal} id="remove-packing-item-modal" heading="Remove component"><s-paragraph>Are you sure you want to remove this component?</s-paragraph><s-button slot="secondary-actions" commandFor="remove-packing-item-modal" command="--hide">Cancel</s-button><s-button slot="primary-action" variant="primary" tone="critical" commandFor="remove-packing-item-modal" command="--hide" onClick={() => { editorModal.current?.hideOverlay(); setDraft(null); onRemove(editingItem.id); }}>Remove component</s-button></s-modal>}
    </s-stack>
  );
}

function NativeOptionEditor({product, optionId, onUpdate}) {
  const optionIndex = product.configuration.shopifyOptions.findIndex(({id}) => id === optionId);
  if (optionIndex < 0) return <s-banner tone="critical">This Shopify option is no longer available.</s-banner>;
  const shopifyOption = product.configuration.shopifyOptions[optionIndex];
  const sourceOption = product.options.find(({id}) => id === shopifyOption.shopifyOptionId);
  return (
    <s-stack direction="block" gap="base">
      <s-text-area label="Help text" rows={3} value={shopifyOption.description || ''} onInput={(event) => onUpdate((configuration) => { configuration.shopifyOptions[optionIndex].description = event.currentTarget.value; })} />
      {(sourceOption?.optionValues || []).map((value) => { const valueId = numericId(value.id); return <s-box key={value.id} padding="base" background="subdued" borderRadius="base"><s-stack direction="block" gap="small"><s-text type="strong">{value.name}</s-text><s-text-field label="Description" value={shopifyOption.values?.[valueId]?.description || ''} onInput={(event) => onUpdate((configuration) => { const option = configuration.shopifyOptions[optionIndex]; option.values ||= {}; option.values[valueId] ||= {includeItemIds: []}; option.values[valueId].description = event.currentTarget.value; })} /></s-stack></s-box>; })}
    </s-stack>
  );
}

function newAddonGroup(existing) {
  return newCustomOption(existing);
}

function moveTo(items, from, to) {
  const [item] = items.splice(from, 1);
  items.splice(to, 0, item);
}

function orderLabel(item, product) {
  if (item.type === 'shopifyOption') return product.configuration.shopifyOptions.find(({id}) => id === item.id)?.name || 'Shopify option';
  return product.configuration.customOptions.find(({id}) => id === item.id)?.label || 'Custom option';
}

function orderType(item) {
  if (item.type === 'shopifyOption') return 'Shopify option';
  return 'Custom option';
}

function orderSummary(item, product) {
  if (item.type === 'shopifyOption') {
    const configuredOption = product.configuration.shopifyOptions.find(({id}) => id === item.id);
    const shopifyOption = product.options.find(({id}) => id === configuredOption?.shopifyOptionId);
    if (!shopifyOption) return '—';
    const count = shopifyOption.optionValues.length;
    return `${count} ${count === 1 ? 'choice' : 'choices'}`;
  }
  if (item.type !== 'customOption') return '—';
  const group = product.configuration.customOptions.find(({id}) => id === item.id);
  if (!group) return '—';
  return `${group.choices.length} ${group.choices.length === 1 ? 'choice' : 'choices'} · ${group.minSelections > 0 ? 'Required' : 'Optional'}`;
}

function orderKey(item) {
  return `${item.type}:${item.id || ''}`;
}

function removeAddonGroup(configuration, groupId) {
  configuration.customOptions = configuration.customOptions.filter(({id}) => id !== groupId);
  configuration.formOrder = configuration.formOrder.filter((item) => !(item.type === 'customOption' && item.id === groupId));
  for (const group of configuration.customOptions) {
    group.conditions.rules = (group.conditions?.rules || []).filter((rule) => !(rule.source === 'customOption' && rule.sourceId === groupId));
  }
}

function removeIncludeItem(configuration, itemId) {
  configuration.includedItems = configuration.includedItems.filter(({id}) => id !== itemId);
  configuration.includedItemOrder = (configuration.includedItemOrder || []).filter((key) => key !== `include:${itemId}`);
  for (const option of configuration.shopifyOptions) for (const key of Object.keys(option.values || {})) { const value = option.values[key]; value.includeItemIds = (value.includeItemIds || []).filter((id) => id !== itemId); }
  for (const group of configuration.customOptions) for (const choice of group.choices) choice.includeItemIds = (choice.includeItemIds || []).filter((id) => id !== itemId);
}

function numericId(gid) { return gid.split('/').pop(); }
